import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { CustomerPriceEvaluation } from '@ravago/shared/page-data/models/elements';
import { ProductOrder } from '@ravago/shared/page-data/models/features/product-order.model';
import { Quotation, QuotationDetail, QuotationLine, QuotationStatus } from '@ravago/shared/page-data/models/features/product-quotation.model';
import { UnitPrice } from '@ravago/shared/translations';

import { compareDesc } from 'date-fns/compareDesc';

import {
   ProductPriceCardItem,
   ProductPriceCardSmallLotPricing
} from '../../components/content/my-products-and-prices/models/product-price-card-item.model';

export interface QuotationGroup {
   id: string;
   name: string;
   status: QuotationStatus;
   minUnitPrice: UnitPrice;
   maxUnitPrice: UnitPrice | undefined;
   packaging: string | undefined;
   isPriceFixed?: boolean;
}

const validColors = [
   'amber',
   'artic white',
   'beige',
   'black',
   'blue',
   'blue ice',
   'brilliant white',
   'bronze',
   'brown',
   'cash register white',
   'clear',
   'clear blue',
   'crystal clear',
   'crystal oxidised',
   'cyan',
   'gold',
   'green',
   'greige',
   'grey',
   'ivory',
   'light',
   'light blue',
   'metallic',
   'mixed colors',
   'natural',
   'natural black',
   'natural oxidised',
   'natural white',
   'opal',
   'orange',
   'pink',
   'purple',
   'red',
   'seperate',
   'silver',
   'stone effect',
   'stryker effect',
   'terracotta',
   'translucent',
   'transparent',
   'turquoise',
   'twilight black',
   'violet',
   'violet trans',
   'white',
   'water white',
   'white trans',
   'wild rice',
   'yellow',
   'other',
   'custom'
];

const getColorFromProductDescription = (description: string): string | undefined => {
   const descriptionWords = description.trim().toLowerCase().split(/\s+/);
   const foundColor = validColors.find((colorName) => descriptionWords.includes(colorName));
   return foundColor?.toUpperCase();
};

const sortProductPriceCardItems = (items: ProductPriceCardItem[]): ProductPriceCardItem[] =>
   items.sort((a, b) => {
      if (a.lowestUnitPrice && !b.lowestUnitPrice) return -1;
      if (!a.lowestUnitPrice && b.lowestUnitPrice) return 1;

      if (a.lastOrderedOn && !b.lastOrderedOn) return -1;
      if (!a.lastOrderedOn && b.lastOrderedOn) return 1;
      if (!a.lastOrderedOn && !b.lastOrderedOn) return 0;

      return a.lastOrderedOn && b.lastOrderedOn ? compareDesc(a.lastOrderedOn, b.lastOrderedOn) : 0;
   });

const getLowestOfflineUnitPrice = (quotationLines: QuotationLine[]): UnitPrice | undefined => {
   const offlinePrices = quotationLines.map((line) => line.offlineUnitPrice).filter((unitPrice): unitPrice is UnitPrice => !!unitPrice);
   if (offlinePrices.length === 0) return undefined;
   return offlinePrices.reduce((min, curr) => (curr.price.amount < min.price.amount ? curr : min), offlinePrices[0]);
};

const getLowestUnitPriceIsFixed = (quotationLines: QuotationLine[], lowestUnitPrice: UnitPrice | undefined): boolean | undefined => {
   if (!lowestUnitPrice) return undefined;

   const lowestPriceLines = quotationLines.filter(
      (line) =>
         line.unitPrice.price.amount === lowestUnitPrice.price.amount &&
         line.unitPrice.price.currency === lowestUnitPrice.price.currency &&
         line.unitPrice.unitOfMeasure === lowestUnitPrice.unitOfMeasure
   );

   return lowestPriceLines.every((line) => line.isPriceFixed !== false);
};

const getDistinctCustomerReferences = (quotationLines: QuotationLine[]): string[] => {
   const references = quotationLines.map((line) => line.customerProductReference).filter((ref): ref is string => !!ref && ref.trim().length > 0);
   return Array.from(new Set(references));
};

const pickSmallLotPricing = (evaluation: CustomerPriceEvaluation | undefined): ProductPriceCardSmallLotPricing | undefined => {
   if (!evaluation || evaluation.tiers.length === 0) return undefined;
   const lowestTier = evaluation.tiers.reduce((min, t) => (t.discountedPrice.amount < min.discountedPrice.amount ? t : min), evaluation.tiers[0]);
   return { lowestTier, packaging: evaluation.packaging };
};

export const generateProductPriceCardItems = (
   products: ProductOrder[],
   quotations: Quotation[],
   contentfulProducts: NormalizedProduct[],
   showOfflinePrices: boolean,
   evaluations: CustomerPriceEvaluation[] = []
): ProductPriceCardItem[] => {
   const result: ProductPriceCardItem[] = [];

   const evaluationByProductId = new Map<number, CustomerPriceEvaluation>(evaluations.map((e) => [e.ohmProductId, e]));

   const filteredQuotations = showOfflinePrices
      ? quotations
      : quotations.filter((quotation) => !quotation.quotationLines.every((line) => line.offlineUnitPrice));

   const contentfulProductMap = contentfulProducts.reduce((acc: Record<string, NormalizedProduct>, curr) => {
      const key = curr.id.split('-')[1];
      acc[key] = curr;
      return acc;
   }, {});

   filteredQuotations.forEach((value) => {
      const matchingProductOrder = products.find((product) => product.id === value.product.id);
      const matchingContentfulProduct = value.product.baseProductId ? contentfulProductMap[value.product.baseProductId] : undefined;
      const color = value.product.color?.name || matchingProductOrder?.color || getColorFromProductDescription(value.product.description);
      const colorId = value.product.color?.id || matchingProductOrder?.colorId;
      const producerColorCode = value.product.color?.producerColorCode || matchingProductOrder?.producerColor;
      const grade = value.product.grade || matchingProductOrder?.grade;
      const brandName = value.product.brand || matchingProductOrder?.brand || matchingContentfulProduct?.brand.name;

      const filteredQuotationLines = showOfflinePrices ? value.quotationLines : value.quotationLines.filter((line) => !line.offlineUnitPrice);

      const customerReferences = getDistinctCustomerReferences(filteredQuotationLines);

      result.push({
         productId: value.product.id,
         baseProductId: value.product.baseProductId,
         color,
         colorId,
         producerColorCode,
         grade,
         brandName,
         logoUrl: matchingContentfulProduct?.brand.producer?.logo?.url,
         slug: matchingContentfulProduct?.slug,
         description: value.product.description,
         customerReferences,
         businessContextReference: matchingProductOrder?.id,
         lastOrderedOn: matchingProductOrder?.lastOrder ? new Date(matchingProductOrder.lastOrder.orderedOn) : undefined,
         lowestUnitPrice: value.lowestUnitPrice,
         isPriceFixed: getLowestUnitPriceIsFixed(filteredQuotationLines, value.lowestUnitPrice),
         lowestOfflineUnitPrice: getLowestOfflineUnitPrice(filteredQuotationLines),
         polymerType: matchingContentfulProduct?.polymerSubGroup ?? undefined,
         packaging: value.product.packaging,
         quotationLines: filteredQuotationLines
      });
   });

   products.forEach((value) => {
      const matchingContentfulProduct = value.baseProductId ? contentfulProductMap[value.baseProductId] : undefined;

      if (result.some((item) => item.productId === value.id)) {
         return;
      }

      result.push({
         productId: value.id,
         baseProductId: value.baseProductId,
         color: value.color,
         colorId: value.colorId,
         producerColorCode: value.producerColor,
         grade: value.grade,
         brandName: value.brand,
         logoUrl: matchingContentfulProduct?.brand.producer?.logo?.url,
         slug: matchingContentfulProduct?.slug,
         description: value.description,
         customerReferences: value.customerProductReference ? [value.customerProductReference] : [],
         businessContextReference: value.id,
         lastOrderedOn: value.lastOrder ? new Date(value.lastOrder.orderedOn) : undefined,
         polymerType: matchingContentfulProduct?.polymerSubGroup ?? undefined,
         smallLotPricing: pickSmallLotPricing(evaluationByProductId.get(value.id))
      });
   });

   return sortProductPriceCardItems(result);
};

export const generateQuoteProductPriceCardItems = (
   quote: QuotationDetail,
   relatedProducts: ProductOrder[],
   contentfulProducts: NormalizedProduct[],
   showOfflinePrices: boolean
): ProductPriceCardItem[] => {
   const filteredProducts = showOfflinePrices
      ? quote.products
      : quote.products.filter((quoteProduct) => !quoteProduct.quotationLines.every((line) => line.offlineUnitPrice));

   const items = filteredProducts.map((quoteProduct) => {
      const contentfulProductId = `product-${quoteProduct.baseProductId}`;
      const matchingContentfulProduct = contentfulProducts.find((product) => product.id === contentfulProductId);
      const matchingProductOrder = relatedProducts.find((product) => product.id === quoteProduct.id);
      const color = quoteProduct.color?.name || matchingProductOrder?.color || getColorFromProductDescription(quoteProduct.description);
      const colorId = quoteProduct.color?.id || matchingProductOrder?.colorId;
      const producerColorCode = quoteProduct.color?.producerColorCode || matchingProductOrder?.producerColor;
      const grade = quoteProduct.grade || matchingProductOrder?.grade;
      const brandName = quoteProduct.brand || matchingContentfulProduct?.brand.name;

      const filteredQuotationLines = showOfflinePrices
         ? quoteProduct.quotationLines
         : quoteProduct.quotationLines.filter((line) => !line.offlineUnitPrice);

      const matchingQuotationDetailProduct = quote.products.find((product) =>
         product.quotationLines.some((line) => filteredQuotationLines.some((quoteLine) => quoteLine.id === line.id))
      );

      const quotationLines: QuotationLine[] =
         matchingQuotationDetailProduct?.quotationLines.map((line) => ({
            ...line,
            quotation: {
               id: quote.id,
               status: quote.status,
               name: quote.name
            }
         })) || [];

      const customerReferences = getDistinctCustomerReferences(quotationLines);

      return {
         productId: quoteProduct.id,
         baseProductId: quoteProduct.baseProductId,
         color,
         colorId,
         producerColorCode,
         grade,
         brandName,
         logoUrl: matchingContentfulProduct?.brand.producer?.logo?.url,
         slug: matchingContentfulProduct?.slug,
         description: quoteProduct.description,
         customerReferences,
         businessContextReference: matchingProductOrder?.id,
         lastOrderedOn: matchingProductOrder?.lastOrder ? new Date(matchingProductOrder.lastOrder.orderedOn) : undefined,
         lowestUnitPrice: matchingQuotationDetailProduct?.lowestUnitPrice,
         isPriceFixed: quote.isPriceFixed,
         lowestOfflineUnitPrice: getLowestOfflineUnitPrice(quotationLines),
         polymerType: matchingContentfulProduct?.polymerSubGroup,
         packaging: matchingQuotationDetailProduct?.packaging,
         quotationLines
      } as ProductPriceCardItem;
   });

   return sortProductPriceCardItems(items);
};

export const groupQuotationLines = (quotationLines: QuotationLine[] | undefined, packaging: string | undefined): QuotationGroup[] => {
   if (!quotationLines?.length) return [];

   type Entry = { id: string; name: string; status: QuotationStatus; min: UnitPrice; max: UnitPrice; isPriceFixed?: boolean };
   const map = new Map<string, Entry>();

   for (const { quotation, unitPrice, isPriceFixed } of quotationLines) {
      const entry = map.get(quotation.id);
      if (entry) {
         if (unitPrice.price.amount < entry.min.price.amount) entry.min = unitPrice;
         if (unitPrice.price.amount > entry.max.price.amount) entry.max = unitPrice;
         entry.isPriceFixed = isPriceFixed;
      } else {
         map.set(quotation.id, {
            id: quotation.id,
            name: quotation.name,
            status: quotation.status,
            min: unitPrice,
            max: unitPrice,
            isPriceFixed
         });
      }
   }

   return Array.from(map.values(), ({ min, max, isPriceFixed, ...group }) => {
      const quotationGroup = {
         ...group,
         packaging,
         minUnitPrice: min,
         maxUnitPrice: min.price.amount !== max.price.amount ? max : undefined
      };

      return { ...quotationGroup, isPriceFixed };
   });
};
